import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useAppSelector, useAppDispatch } from "store/index";
import {
  deleteTournamentsItem,
  getTournamentsList,
  handlePaginate,
  handleSelectAll,
  handleSelectItem,
  updateTournamentsBunch,
} from "store/liveOps/tournaments/slice";
import { tournamentsListLimits } from "configs/limits";
import { ModalTypes } from "configs/types";
import { openModal } from "store/modals/slice";

import DefaultDropdown from "components/dropdowns/DefaultDropdown/DefaultDropdown";
import Pagination from "components/Pagination";
import ActionBtn from "components/buttons/ActionBtn";
import TournamentsFilter from "./TournamentsFilter";

import {
  NotificationTypes,
  showNotification,
} from "../../../store/notifications/slice";
import { Table } from "components/table/Table";
import EmptyState from "components/EmptyState/EmptyState";
import DataTableSkeleton from "components/global/Skeleton/TableSkeleton/DataTableSkeleton";
import PaginationSkeleton from "components/global/Skeleton/PaginationSkeleton/PaginationSkeleton";
import { usePermissionHelper } from "helpers/usePermissionHelper";
import { RefreshButton } from "components/buttons/RefreshButton";
import { useRefreshList } from "helpers/useRefreshList";
import styles from "./List.module.scss";

const TournamentsList: React.FC = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [order, setOrder] = useState<{
    title: string;
    isActive: string;
    currentMembersCount: string;
    id: string;
  }>({
    title: "none",
    isActive: "none",
    currentMembersCount: "none",
    id: "none",
  });
  const [currentLimit, setLimit] = useState<number>(tournamentsListLimits[0]);
  const [search, setSearch] = useState<string>("");
  const [Status, setStatus] = useState<
    { value: number | string; label: string }[]
  >([]);
  const [Currency, setCurrency] = useState<
    { value: number | string; label: string }[]
  >([]);
  const [active, setActive] = useState<
    { value: number | string; label: string }[]
  >([{ value: 3, label: "All" }]);

  const options = [
    {
      label: t("activate"),
      value: "activate",
    },
    {
      label: t("hide"),
      value: "hide",
    },
  ];

  const list = useAppSelector((store) => store.tournaments.list);
  const isListFetched = useAppSelector(
    (store) => store.tournaments.isListFetched,
  );
  const totalItems = useAppSelector((store) => store.tournaments.totalItems);
  const totalPages = useAppSelector((store) => store.tournaments.totalPages);
  const activePage = useAppSelector((store) => store.tournaments.activePage);
  const selectedItems = useAppSelector(
    (store) => store.tournaments.selectedItems,
  );
  const processingItemId = useAppSelector(
    (store) => store.tournaments.processingItemId,
  );
  const permissions = usePermissionHelper();

  const { refresh, isRefreshRequested } = useRefreshList({
    sliceName: 'tournaments',
    isListFetched
  });

  useEffect(() => {
    if (!isListFetched) {
      dispatch(
        getTournamentsList({
          limit: currentLimit,
          offset: activePage - 1,
          title: search,
          isActive:
            active[0] && active[0].value !== 3
              ? (active[0].value as number)
              : undefined,
          statusIds: Status.map((el) => el.value) as number[],
          currencies: Currency.map((el) => el.value) as string[],
          order,
        }),
      );
    }
  }, [isListFetched, activePage, currentLimit, order]);

  const handleAllClick = () => {
    dispatch(handleSelectAll());
  };
  const handleItemClick = (id: number) => {
    dispatch(handleSelectItem(id));
  };
  const handlePrevClick = () => {
    if (activePage > 1) {
      dispatch(handlePaginate(activePage - 1));
    }
  };
  const handleNextClick = () => {
    if (activePage !== totalPages) {
      dispatch(handlePaginate(activePage + 1));
    }
  };

  const handleActionClick = async (e: string) => {
    try {
      await dispatch(
        updateTournamentsBunch({
          ids: selectedItems,
          isActive: e === "activate" ? 1 : 0,
        }),
      );
    } catch (error) {
      console.log(error, "error");
    }
  };

  const handleOpenEditModal = (id: number) => {
    navigate(
      `${location.pathname}?activeModal=${ModalTypes.tournaments}&entityId=${id}`,
    );
    dispatch(openModal({ activeModal: ModalTypes.tournaments }));
  };
  const handleOpenCreateModal = () => {
    navigate(`${location.pathname}?activeModal=${ModalTypes.tournaments}`);
    dispatch(openModal({ activeModal: ModalTypes.tournaments }));
  };

  const handleOrderClick = (key: keyof typeof order) => {
    const current = order[key];
    let next: "asc" | "desc" | "none";

    if (current === "none") next = "asc";
    else if (current === "asc") next = "desc";
    else next = "none";

    const newOrder = {
      [key]: next,
      ...Object.fromEntries(Object.entries(order).filter(([k]) => k !== key)),
    };
    // @ts-ignore
    setOrder(newOrder);
    dispatch(handlePaginate(1));
  };

  const handleDeleteItem = async (id: number) => {
    const result = await dispatch(deleteTournamentsItem(id ?? 0));
    if (result?.payload === true) {
      dispatch(
        showNotification({
          title: t("success"),
          message: t("entityDeleteSuccess"),
          type: NotificationTypes.success,
        }),
      );
    } else {
      dispatch(
        showNotification({
          title: t("error"),
          message: t("entityDeleteError"),
          type: NotificationTypes.error,
        }),
      );
    }
  };

  const actionProps = permissions?.update
    ? {
        onEdit: (id: number) => handleOpenEditModal(id),
        onView: undefined,
      }
    : {
        onEdit: undefined,
        onView: (id: number) => handleOpenEditModal(id),
      };

  return (
    <div className="page-list-wrapper page-list--with-filter">
      <TournamentsFilter
        handleApply={() => {
          dispatch(handlePaginate(1));
        }}
        searchValue={search}
        handleSearch={setSearch}
        selectedActive={active}
        selectedCurrency={Currency}
        selectedStatus={Status}
        handleUpdateActive={setActive}
        handleUpdateCurrency={setCurrency}
        handleUpdateStatus={setStatus}
      />
      <div className="page-list--with-filter-content">
        <div className="page-list-header">
          <div className="page-list-header--box">
            {permissions?.create && (
              <ActionBtn
                txt={t("addTournament")}
                handleClick={() => {
                  handleOpenCreateModal();
                }}
              />
            )}
          </div>
          <div className="page-list-header--box page-list-header--box--right">
            <RefreshButton
              position="right"
              disabled={isRefreshRequested}
              isLoading={isRefreshRequested}
              onClick={() => refresh()}
            />
          </div>
          <div className="page-list-header--box">
            <DefaultDropdown
              onSelect={(e) => {
                handleActionClick(e as string);
              }}
              placeholder={t("chooseAction")}
              options={options}
              isSmall
              disabled={selectedItems.length === 0 || !permissions?.update}
            />
          </div>
        </div>

        {!isListFetched && (
          <>
            <DataTableSkeleton columnsCount={10} rowsCount={10} />
            <PaginationSkeleton />
          </>
        )}

        {isListFetched && (
          <>
            <div className="game-table-wrap">
              {list.length === 0 && (
                <EmptyState message={t("noTournamentsFound")} />
              )}

              {list.length > 0 && (
                <Table
                  data={list}
                  order={order}
                  idField="id"
                  className={styles.tournamentsTable}
                  columns={{
                    hiddenColumns: ["portraitImage"],
                  }}
                  customColumns={(tournament) => ({
                    description: (
                      <span
                        className={styles.descriptionCell}
                        title={tournament.description}
                      >
                        {tournament.description}
                      </span>
                    ),
                  })}
                  selectedItems={selectedItems}
                  onAllSelect={handleAllClick}
                  onSelect={(id) => handleItemClick(id)}
                  onDelete={(id) => handleDeleteItem(id)}
                  onSort={(id) => (id ? handleOrderClick(id) : {})}
                  processingId={processingItemId}
                  hasDelete={permissions?.delete}
                  hasEdit={permissions?.update}
                  hasView={permissions?.read && !permissions?.update}
                  hasOptions={permissions?.read || permissions?.update}
                  {...actionProps}
                />
              )}
            </div>

            {list.length > 0 && (
              <Pagination
                handleLimitClick={(e) => {
                  setLimit(e);
                  dispatch(handlePaginate(1));
                }}
                limitsArray={tournamentsListLimits}
                activePage={activePage}
                limit={currentLimit}
                totalItems={totalItems}
                totalPages={totalPages}
                handleNextClick={handleNextClick}
                handlePrevClick={handlePrevClick}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default TournamentsList;
